// src/components/Navbar/index.jsx  (or wherever your Navbar lives)
import React, {
    useState,
    useEffect,
    useRef
} from "react";
import "./NavbarElements";
import "./MobileDropdown.css";
import {
    useAuth
} from "../../context/AuthContext";
import {
    Nav,
    NavLink,
    Bars,
    NavMenu,
    NavBtn,
    NavBtnLink,
    DropdownContainer,
    DropdownMenu,
} from "./NavbarElements";
import {
    Link,
    useNavigate
} from "react-router-dom";
import DropdownItem from "./DropdownItem";
import {
    FaShoppingCart,
    FaSignOutAlt
} from "react-icons/fa";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.goharesadaf.ir";

const Navbar = () => {
        const [scrolled, setScrolled] = useState(false);
        const [showProductsDropdown, setShowProductsDropdown] = useState(false);
        const [showUserDropdown, setShowUserDropdown] = useState(false);
        const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
        const [categories, setCategories] = useState([]);
        const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);
        const [cartCount, setCartCount] = useState(0);

        const userRef = useRef(null);
        const productsRef = useRef(null);
        const mobileRef = useRef(null);
        const navigate = useNavigate();

        // Auth context
        const {
            user,
            loadingUser,
            logout
        } = useAuth();

        // scroll styling
        useEffect(() => {
            const onScroll = () => setScrolled(window.scrollY > 50);
            window.addEventListener("scroll", onScroll, {
                passive: true
            });
            return () => window.removeEventListener("scroll", onScroll);
        }, []);

        // fetch cart whenever user (or token) changes so it updates immediately after login/logout
        useEffect(() => {
            let mounted = true;
            const controller = new AbortController();
            const token = localStorage.getItem("token");
            if (!token) {
                setCartCount(0);
                return () => {
                    mounted = false;
                    controller.abort();
                };
            }

            (async () => {
                try {
                    const res = await fetch(`${API_BASE}/api/cart`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            Accept: "application/json",
                        },
                        signal: controller.signal,
                    });
                    if (!res.ok) throw new Error("Failed to fetch cart");
                    const data = await res.json();
                    if (!mounted) return;
                    if (Array.isArray(data.items)) {
                        setCartCount(data.items.reduce((sum, item) => sum + (item.quantity || 0), 0));
                    }
                } catch (err) {
                    if (err.name !== "AbortError") console.error("Error fetching cart:", err);
                }
            })();

            return () => {
                mounted = false;
                controller.abort();
            };
        }, [user]); // re-run when auth user changes

        // fetch categories (simple cache pattern)
        useEffect(() => {
            let mounted = true;
            const controller = new AbortController();

            async function getCategories() {
                try {
                    const res = await fetch(`${API_BASE}/api/admin/categories`, {
                        signal: controller.signal
                    });
                    if (!res.ok) throw new Error("Failed to fetch categories");
                    const data = await res.json();
                    if (!mounted) return;
                    const mapped = data.map((cat) => mapCategory(cat));
                    setCategories(mapped);
                } catch (err) {
                    if (err.name !== "AbortError") console.error("Error fetching categories:", err);
                }
            }
            getCategories();

            return () => {
                mounted = false;
                controller.abort();
            };
        }, []);

        const mapCategory = (cat, parentPath = "") => {
            let currentPath = parentPath ? `${parentPath}/${cat.slug}` : `/products/${cat.slug}`;
            return {
                title: cat.name,
                path: currentPath.replace(/\/{2,}/g, "/"),
                subCategories: cat.children ? cat.children.map((c) => mapCategory(c, currentPath)) : [],
            };
        };

        // Close dropdowns on click outside or on Esc
        useEffect(() => {
            function onDocClick(e) {
                if (userRef.current && !userRef.current.contains(e.target)) {
                    setShowUserDropdown(false);
                }
                if (productsRef.current && !productsRef.current.contains(e.target)) {
                    setShowProductsDropdown(false);
                }
                if (mobileRef.current && !mobileRef.current.contains(e.target)) {
                    // don't auto-close mobile menu here; keep user in control
                }
            }

            function onKey(e) {
                if (e.key === "Escape") {
                    setShowUserDropdown(false);
                    setShowProductsDropdown(false);
                    setMobileMenuOpen(false);
                }
            }
            document.addEventListener("click", onDocClick);
            document.addEventListener("keydown", onKey);
            return () => {
                document.removeEventListener("click", onDocClick);
                document.removeEventListener("keydown", onKey);
            };
        }, []);

        // use context logout
        const handleLogout = async () => {
            if (!window.confirm("آیا از خروج از حساب کاربری مطمئن هستید؟")) return;
            try {
                await logout();
            } catch (err) {
                console.warn("logout failed", err);
            } finally {
                setShowUserDropdown(false);
                navigate("/auth");
            }
        };

        // Helper to open user dropdown (toggle)
        const toggleUserDropdown = (e) => {
            e.stopPropagation();
            setShowUserDropdown((s) => !s);
            setShowProductsDropdown(false);
        };

        const toggleProductsDropdown = (e) => {
            e.stopPropagation();
            setShowProductsDropdown((s) => !s);
        };

        return ( <
                Nav scrolled = {
                    scrolled
                }
                className = "site-nav"
                style = {
                    {
                        cursor: "url('/images/shell.png'), auto"
                    }
                } > { /* logo */ } <
                Link to = "/"
                className = "nav-logo" >
                <
                img src = "/images/logo.png"
                alt = "Goharsadaf" / >
                <
                /Link>

                <
                Bars onClick = {
                    () => setMobileMenuOpen((m) => !m)
                }
                aria-label = "باز/بسته کردن منوی موبایل" / >
                <
                NavLink to = "/cart"
                className = "cart-link mobile-cart"
                aria-label = "سبد خرید" >
                <
                FaShoppingCart size = {
                    20
                }
                /> {
                    cartCount > 0 && < span className = "cart-badge" > {
                            cartCount.toLocaleString("ar-EG")
                        } < /span>} <
                        /NavLink>

                        <
                        NavMenu className = {
                            `nav-menu ${mobileMenuOpen ? "open" : ""}`
                        }
                    ref = {
                            mobileRef
                        } >
                        <
                        NavLink to = "/" > صفحه اصلی < /NavLink>

                        <
                        DropdownContainer
                    ref = {
                        productsRef
                    }
                    onMouseEnter = {
                        () => setShowProductsDropdown(true)
                    }
                    onMouseLeave = {
                            () => setShowProductsDropdown(false)
                        } >
                        <
                        NavLink as = "div"
                    to = "/products/"
                    onClick = {
                        toggleProductsDropdown
                    }
                    aria-haspopup = "menu"
                    aria-expanded = {
                            showProductsDropdown
                        } >
                        دسته‌بندی محصولات▾ <
                        /NavLink>

                    {
                        showProductsDropdown && ( <
                            DropdownMenu role = "menu"
                            className = "products-dropdown" > {
                                categories.map((item, index) => ( <
                                    DropdownItem key = {
                                        index
                                    }
                                    item = {
                                        item
                                    }
                                    />
                                ))
                            } <
                            /DropdownMenu>
                        )
                    } <
                    /DropdownContainer>

                    <
                    NavLink to = "/blogs" > بلاگ < /NavLink> <
                        NavLink to = "/training" > آموزش < /NavLink> <
                        NavLink to = "/events" > نمایشگاه ها < /NavLink> <
                        NavLink to = "/team" > درباره ما < /NavLink> <
                        NavLink to = "/contact" > تماس با ما < /NavLink> <
                        /NavMenu>

                        <
                        NavBtn className = "nav-actions" > { /* Cart */ } <
                        NavLink to = "/cart"
                    className = "cart-link"
                    aria-label = "سبد خرید" >
                        <
                        FaShoppingCart size = {
                            20
                        }
                    /> {
                        cartCount > 0 && < span className = "cart-badge" > {
                                cartCount.toLocaleString("ar-EG")
                            } < /span>} <
                            /NavLink>

                        { /* User area */ } <
                        div className = "user-area"
                        ref = {
                            userRef
                        } > {
                            user ? (
                                // 1) If we already have user, show profile immediately
                                <
                                div className = "user-trigger"
                                onClick = {
                                    toggleUserDropdown
                                }
                                role = "button"
                                tabIndex = {
                                    0
                                }
                                aria-haspopup = "menu"
                                aria-expanded = {
                                    showUserDropdown
                                } >
                                <
                                img src = {
                                    user.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || "U")}`
                                }
                                alt = {
                                    user.name || "User"
                                }
                                className = "user-avatar" /
                                >
                                <
                                span className = "user-name" > {
                                    user.name
                                } < /span> <
                                span className = "chev" > ▾ < /span> <
                                /div>
                            ) : loadingUser ? (
                                // 2) No user yet but we're still confirming token -> placeholder
                                <
                                div className = "nav-placeholder"
                                aria-hidden style = {
                                    {
                                        width: 140,
                                        height: 36
                                    }
                                }
                                />
                            ) : (
                                // 3) Not loading and no user -> show login button
                                <
                                NavBtnLink to = "/auth"
                                className = "login-btn" > ورود / ثبت‌نام < /NavBtnLink>
                            )
                        }

                        {
                            showUserDropdown && user && ( <
                                DropdownMenu role = "menu"
                                className = "user-dropdown" >
                                <
                                li > < Link to = "/profile"
                                className = "dropdown-link"
                                onClick = {
                                    () => setShowUserDropdown(false)
                                } > پروفایل < /Link></li >
                                <
                                li > < Link to = "/orders"
                                className = "dropdown-link"
                                onClick = {
                                    () => setShowUserDropdown(false)
                                } > سفارش‌ها < /Link></li >
                                <
                                li > < Link to = "/wishlist"
                                className = "dropdown-link"
                                onClick = {
                                    () => setShowUserDropdown(false)
                                } > علاقه‌مندی‌ها < /Link></li >
                                <
                                li className = "dropdown-sep" / >
                                <
                                li >
                                <
                                button className = "logout-btn"
                                onClick = {
                                    handleLogout
                                }
                                title = "خروج" >
                                <
                                FaSignOutAlt style = {
                                    {
                                        marginInlineEnd: 8
                                    }
                                }
                                /> خروج <
                                /button> <
                                /li> <
                                /DropdownMenu>
                            )
                        } <
                        /div>

                        <
                        /NavBtn>

                        { /* Mobile menu overlay (fallback) */ } {
                            mobileMenuOpen && ( <
                                div className = "mobile-menu-panel"
                                role = "dialog"
                                aria-modal = "true" >
                                <
                                div className = "mobile-menu-inner" >

                                <
                                div className = "mobile-menu-header" >
                                <
                                button type = "button"
                                className = "mobile-close-btn"
                                onClick = {
                                    () => setMobileMenuOpen(false)
                                }
                                aria-label = "بستن منو" >
                                ✕
                                <
                                /button> <
                                /div>

                                <
                                div className = "mobile-links" >
                                <
                                Link to = "/"
                                onClick = {
                                    () => setMobileMenuOpen(false)
                                } > صفحه اصلی < /Link> <
                                div className = "mobile-category-toggle" >
                                <
                                button type = "button"
                                onClick = {
                                    () => setMobileCategoriesOpen((prev) => !prev)
                                }
                                className = "mobile-category-btn" >
                                دسته‌بندی محصولات {
                                    mobileCategoriesOpen ? "▴" : "▾"
                                } <
                                /button>

                                {
                                    mobileCategoriesOpen && ( <
                                        ul className = "mobile-categories" > {
                                            categories.map((cat, idx) => ( <
                                                li key = {
                                                    idx
                                                } >
                                                <
                                                Link to = {
                                                    cat.path
                                                }
                                                onClick = {
                                                    () => setMobileMenuOpen(false)
                                                } >
                                                {
                                                    cat.title
                                                } <
                                                /Link> {
                                                    cat.subCategories.length > 0 && ( <
                                                        ul className = "mobile-subcategories" > {
                                                            cat.subCategories.map((sub, subIdx) => ( <
                                                                li key = {
                                                                    subIdx
                                                                } >
                                                                <
                                                                Link to = {
                                                                    sub.path
                                                                }
                                                                onClick = {
                                                                    () => setMobileMenuOpen(false)
                                                                } >
                                                                {
                                                                    sub.title
                                                                } <
                                                                /Link> <
                                                                /li>
                                                            ))
                                                        } <
                                                        /ul>
                                                    )
                                                } <
                                                /li>
                                            ))
                                        } <
                                        /ul>
                                    )
                                } <
                                /div> <
                                Link to = "/cart"
                                onClick = {
                                    () => setMobileMenuOpen(false)
                                } > سبد خرید < /Link> <
                                Link to = "/blogs"
                                onClick = {
                                    () => setMobileMenuOpen(false)
                                } > بلاگ < /Link> <
                                Link to = "/training"
                                onClick = {
                                    () => setMobileMenuOpen(false)
                                } > آموزش < /Link> <
                                Link to = "/events"
                                onClick = {
                                    () => setMobileMenuOpen(false)
                                } > نمایشگاه ها < /Link> <
                                Link to = "/team"
                                onClick = {
                                    () => setMobileMenuOpen(false)
                                } > درباره ما < /Link> <
                                Link to = "/contact"
                                onClick = {
                                    () => setMobileMenuOpen(false)
                                } > تماس با ما < /Link> <
                                /div>

                                <
                                div className = "mobile-auth" > {
                                    user ? ( <
                                        >
                                        <
                                        Link to = "/profile"
                                        onClick = {
                                            () => setMobileMenuOpen(false)
                                        }
                                        className = "mobile-profile" >
                                        <
                                        img src = {
                                            user.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || "U")}`
                                        }
                                        alt = {
                                            user.name
                                        }
                                        /> <
                                        div >
                                        <
                                        strong > {
                                            user.name
                                        } < /strong> <
                                        div className = "small-muted" > {
                                            user.email
                                        } < /div> <
                                        /div> <
                                        /Link> <
                                        button className = "mobile-logout"
                                        onClick = {
                                            () => {
                                                setMobileMenuOpen(false);
                                                handleLogout();
                                            }
                                        } > خروج < /button> <
                                        />
                                    ) : loadingUser ? ( <
                                        div className = "mobile-placeholder"
                                        aria-hidden > < /div>
                                    ) : ( <
                                        Link to = "/auth"
                                        onClick = {
                                            () => setMobileMenuOpen(false)
                                        }
                                        className = "mobile-login" > ورود / ثبت‌نام < /Link>
                                    )
                                } <
                                /div>

                                <
                                /div> <
                                /div>
                            )
                        } <
                        /Nav>
                    );
                };

                export default Navbar;